import moment from 'moment-timezone'
import { message } from 'antd'
import {
    DELIVERY_STATUSES,
    OVERSEAS_ORDER_STATUSES,
    UPS_CARRIER_TYPE,
    ORDER_COMPLETE_STATUS,
    SHIPPED_STATUS,
    SHIPMENTS_TAB,
    ORDER_STATUS_TAB,
} from '@Orders/constants'
import { useMemo } from 'react'

import { CONFIG_OPTION_EMPTY } from '~/lib/partsConstants'
import { BLOCKED_POPUPS_DOWNLOAD } from '~/lib/flashMsgs'

import { getLocalStorageItem, TYPE_OF_HTS } from './HTS/HTSLogic'
import {
    salesOrder_salesOrder_salesOrderLines_items as ItemsType,
    salesOrder as SalesOrderType,
    salesOrder_salesOrder_salesOrderLines_items,
    salesOrder_salesOrder_salesOrderLines_parts as PartsType,
} from './Queries/__generated__/salesOrder'
import {
    DONOT_SHOW_UPDATED_TERM_MODAL,
    UPDATED_TERM_START_DATE,
} from './constants'

const {
    UNKNOWN,
    PRE_TRANSIT,
    IN_TRANSIT,
    OUT_FOR_DELIVERY,
    DELIVERED,
    RETURN_TO_SENDER,
    FAILURE,
    CANCELLED,
    ERROR,
} = DELIVERY_STATUSES
const DELIVERY_STATUS_COLOR_MAP = {
    [UNKNOWN]: '',
    [PRE_TRANSIT]: '',
    [IN_TRANSIT]: 'green',
    [OUT_FOR_DELIVERY]: 'green',
    [DELIVERED]: 'green',
    [RETURN_TO_SENDER]: 'red',
    [FAILURE]: 'red',
    [CANCELLED]: 'red',
    [ERROR]: 'red',
}
const DELIVERY_STATUS_TEXT_MAP = {
    [UNKNOWN]: 'Unknown',
    [PRE_TRANSIT]: 'Pre Transit',
    [IN_TRANSIT]: 'In Transit',
    [OUT_FOR_DELIVERY]: 'Out for Delivery',
    [DELIVERED]: 'Delivered',
    [RETURN_TO_SENDER]: 'Return to Sender',
    [FAILURE]: 'Failure',
    [CANCELLED]: 'Cancelled',
    [ERROR]: 'Error',
}

export const CREDIT_CARD = 'cc'
export const PURCHASE_ORDER = 'po'

const PAYMENT_TYPES = {
    [CREDIT_CARD]: 'card',
    [PURCHASE_ORDER]: 'PO',
}

export const HTS_TYPE = 'HTStype'
export const HTS_TAB_TEXT = {
    CUSTOMS_INFORMATION: 'Customs information',
    HTS_CODES: 'HTS Codes',
}
export const getDeliveryStateColor = (state: string) => {
    return DELIVERY_STATUS_COLOR_MAP[state] || ''
}

export const getDeliveryStateText = (state: string) => {
    return DELIVERY_STATUS_TEXT_MAP[state] || state
}

export function openUrlInNewWindow(url: string) {
    if (!url) {
        return
    }
    try {
        window.open(url, '_blank', 'noopener')
    } catch {
        message.warn(BLOCKED_POPUPS_DOWNLOAD)
    }
    return
}

// UPS returns timestamps in the timezone of the package scan
// FedEx returns timestamps in UTC and are converted to users local time
export const getEstDeliveryDate = (date: Date, carrier: string | null) => {
    if (!date) {
        return CONFIG_OPTION_EMPTY
    }
    return carrier === UPS_CARRIER_TYPE
        ? moment.utc(date).format('dddd, MMMM DD, YYYY')
        : moment(date).format('dddd, MMMM DD, YYYY')
}

export const getPaymentType = (paymentdetails: {
    paymentType?: string | null
    creditCardLast4?: string | null
}): string | null => {
    const { paymentType, creditCardLast4 } = paymentdetails
    let paymentTypeText

    switch (paymentType) {
        case CREDIT_CARD:
            paymentTypeText = `${PAYMENT_TYPES[CREDIT_CARD]}${
                creditCardLast4 ? ` ending in ${creditCardLast4}` : ''
            }`
            break

        case PURCHASE_ORDER:
            paymentTypeText = PAYMENT_TYPES[PURCHASE_ORDER]
            break
        default:
            paymentTypeText = paymentType || CONFIG_OPTION_EMPTY
            break
    }

    return paymentTypeText
}

export const isSalesOrderLineProductionRegionOverseas = (
    salesOrderLine: ItemsType,
) =>
    salesOrderLine?.productionRegion &&
    (salesOrderLine.productionRegion === OVERSEAS_ORDER_STATUSES.OVERSEAS ||
        salesOrderLine.productionRegion === OVERSEAS_ORDER_STATUSES.FLEX ||
        salesOrderLine.productionRegion === OVERSEAS_ORDER_STATUSES.SAVER)

export const getDisplayClearanceBanner = ({
    deliveryStatus,
    isInternational,
    htsReleaseDateFlag,
    checked5106,
    allHtsSubmitted,
    htsType,
}: {
    deliveryStatus: string
    isInternational: boolean
    htsReleaseDateFlag: boolean
    checked5106?: boolean | undefined
    allHtsSubmitted?: boolean
    htsType: string
}): boolean => {
    const displayClearanceBanner =
        deliveryStatus !== ORDER_COMPLETE_STATUS &&
        isInternational &&
        !htsReleaseDateFlag &&
        !allHtsSubmitted &&
        !(htsType === TYPE_OF_HTS.COMMERCIAL
            ? checked5106 && allHtsSubmitted
            : allHtsSubmitted)

    return displayClearanceBanner
}

const isCountryCanada = (country?: string) =>
    country === 'Canada' || country === 'CA'

const isInternationalShipping = (
    items: salesOrder_salesOrder_salesOrderLines_items[] | undefined,
) => {
    return items?.some((item: salesOrder_salesOrder_salesOrderLines_items) => {
        return (
            item?.productionRegion === OVERSEAS_ORDER_STATUSES.FLEX ||
            item?.productionRegion === OVERSEAS_ORDER_STATUSES.OVERSEAS ||
            item?.productionRegion === OVERSEAS_ORDER_STATUSES.SAVER
        )
    })
}

export const getIsInternational = (data: SalesOrderType) => {
    const country = data?.salesOrder?.paymentDetails?.billingAddress?.country

    return (
        isInternationalShipping(data?.salesOrder?.salesOrderLines?.items) ||
        isCountryCanada(country)
    )
}

export const isOrderTypeIm = (OrderType: string | null) => {
    return Boolean(OrderType && OrderType === 'im')
}

export const getShouldShowUpdatedHTSTermModal = ({
    isInternational,
    createdAt = '1900-01-01',
}: {
    isInternational: boolean
    createdAt: string
}) => {
    const updatedTermShowLocalState = getLocalStorageItem(
        DONOT_SHOW_UPDATED_TERM_MODAL,
    )

    return (
        isInternational &&
        updatedTermShowLocalState !== 'true' &&
        moment(createdAt).isAfter(moment(UPDATED_TERM_START_DATE))
    )
}

export type shipmentIdMapType = {
    [x: number]: string
}

export type shipmentIdsMapType = shipmentIdMapType[] | []

export const useGetDefaultActiveKey = ({
    salesOrderLines,
}: {
    salesOrderLines?: ItemsType[] | PartsType[] | null | undefined
}) =>
    useMemo(
        () =>
            salesOrderLines &&
            salesOrderLines?.length > 0 &&
            (salesOrderLines || []).every(
                salesOrderLineItem =>
                    salesOrderLineItem.deliveryStatus === SHIPPED_STATUS,
            )
                ? SHIPMENTS_TAB
                : ORDER_STATUS_TAB,
        [salesOrderLines],
    )

export const PROVISIONED_UI_FLAGS = Object.freeze({
    UI_CPQ_QUOTES_INTEGRATION_V2: 'UI_CPQ_QUOTES_INTEGRATION_V2',
    NON_IM_DFM_UI: 'NON_IM_DFM_UI',
})
